import {
  colorRuleLegendEntries,
  resolveUseAsAnalysisAxis,
  type ColorRule,
  type FieldDef,
  type FieldOption,
} from '@citizen-walk/shared-types';
import type { AiCategoryMastersResult } from './useAiCategoryMasters';

/**
 * Phase F — old2 `apps/user/src/components/FilterPanel.tsx` を逐語移植した全画面 bottom-sheet。
 *
 * old2 完全同一にした section (監査確定):
 *   1. キーワード (タイトル・コメント・投稿者名)
 *   2. マーカーの見方 (colorRule の凡例、quadrant/value 両対応)
 *   3. セッション (chip 複数選択、✓ prefix)
 *   4. 投稿項目 (postFields の useAsAnalysisAxis 済 select/multiselect/rating-scale を動的列挙)
 *   5. 投稿者の属性 (userFields 同上)
 *   6. AI解析結果 (M2 category master の chip。old2 は M3 outputFormat 軸だが v2 に loader が無いため
 *      M2 カテゴリ chip で代替。filterPostsByCategory に接続)
 *   7. 日付範囲 (from / to)
 *   8. その他 (写真あり / 最小いいね)
 *
 * 文言は old2 逐語 (「検索・フィルター」「リセット」「閉じる」「キーワード」「マーカーの見方」
 * 「セッション」「投稿項目」「投稿者の属性」「AI解析結果」「日付範囲」「その他」「写真あり」「最小いいね」)。
 *
 * 配色は v2 preset (primary/slate/danger) へ置換 (old2 brand-600 → primary-600、neutral → slate、
 * error-700 → danger-600)。inline style 禁止のため動的色 (凡例ドット) のみ CSS var ではなく
 * 既存 ColorBadge と同パターンの `style={{ background }}` を許容 (markerStyle 由来の動的色)。
 */

/** rating-scale 軸の整数値リストを生成 (old2 getScaleOptions と同一)。 */
function getScaleOptions(field: FieldDef): string[] {
  const min = field.optionsMeta?.min ?? -3;
  const max = field.optionsMeta?.max ?? 3;
  const excludeZero = field.optionsMeta?.excludeZero ?? false;
  const vals: string[] = [];
  for (let i = min; i <= max; i++) {
    if (excludeZero && i === 0) continue;
    vals.push(String(i));
  }
  return vals;
}

/**
 * 検索フィルタに表示すべきフィールドを抽出 (old2 getFilterableFields と同一)。
 * - mode が hidden でない
 * - type が select / multiselect / rating-scale
 * - useAsAnalysisAxis (既定値含む) が true
 */
function getFilterableFields(fields: FieldDef[], scope: 'postFields' | 'userFields'): FieldDef[] {
  return fields.filter((f) => {
    if (f.mode === 'hidden') return false;
    if (f.type !== 'select' && f.type !== 'multiselect' && f.type !== 'rating-scale') return false;
    return resolveUseAsAnalysisAxis(f, scope);
  });
}

/** FieldDef の type に応じて選択肢の (value, label) を返す (old2 getFieldOptions と同一)。 */
function getFieldOptions(f: FieldDef): FieldOption[] {
  if (f.type === 'rating-scale') {
    return getScaleOptions(f).map((v) => ({ value: v, label: v }));
  }
  return f.options;
}

export interface FilterSessionOption {
  id: string;
  name: string;
}

export interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  /** active filter 段数 (FilterButton badge と一致)。リセット button の出し分けにも使用。 */
  activeFilterCount: number;
  onReset: () => void;
  // ── controlled filter state ──
  keyword: string;
  onKeywordChange: (v: string) => void;
  sessions: FilterSessionOption[];
  sessionIds: string[];
  onSessionIdsChange: (ids: string[]) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  hasPhoto: boolean | null;
  onHasPhotoChange: (v: boolean | null) => void;
  minAgreeCount: number;
  onMinAgreeCountChange: (v: number) => void;
  postFieldFilters: Record<string, string[]>;
  onPostFieldFiltersChange: (next: Record<string, string[]>) => void;
  userFieldFilters: Record<string, string[]>;
  onUserFieldFiltersChange: (next: Record<string, string[]>) => void;
  selectedCategoryCodes: string[];
  onSelectedCategoryCodesChange: (codes: string[]) => void;
  // ── data sources ──
  postFields: FieldDef[];
  userFields: FieldDef[];
  colorRule: ColorRule | null;
  categoryMasters: AiCategoryMastersResult;
}

export function FilterPanel({
  open,
  onClose,
  activeFilterCount,
  onReset,
  keyword,
  onKeywordChange,
  sessions,
  sessionIds,
  onSessionIdsChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  hasPhoto,
  onHasPhotoChange,
  minAgreeCount,
  onMinAgreeCountChange,
  postFieldFilters,
  onPostFieldFiltersChange,
  userFieldFilters,
  onUserFieldFiltersChange,
  selectedCategoryCodes,
  onSelectedCategoryCodesChange,
  postFields,
  userFields,
  colorRule,
  categoryMasters,
}: FilterPanelProps) {
  if (!open) return null;

  const filterablePostFields = getFilterableFields(postFields, 'postFields');
  const filterableUserFields = getFilterableFields(userFields, 'userFields');
  const legendEntries = colorRule ? colorRuleLegendEntries(colorRule) : [];
  // AI 解析カテゴリの選択肢: 複合コード (majorCd-categoryCd) を value にして一意化する。
  // categoryCd は大分類内でのみ一意 (例 V-01/N-01/T-01 ... が全て categoryCd="01") のため、
  // categoryCd 単独を value にすると 1 つ選んだだけで同番号の別カテゴリも選択状態になる不具合が出る。
  // 複合コードは投稿の generic aspect_code/object_code (例 "V-05") とも一致し、突合にも使える。
  const categoryOptions: FieldOption[] = (() => {
    const seen = new Set<string>();
    const opts: FieldOption[] = [];
    for (const r of categoryMasters.masters.flatMap((m) => m.rows ?? [])) {
      const code = r.majorCd ? `${r.majorCd}-${r.categoryCd}` : r.categoryCd;
      if (!code || seen.has(code)) continue;
      seen.add(code);
      opts.push({ value: code, label: r.category });
    }
    return opts;
  })();

  function updatePostField(key: string, vals: string[]) {
    const next = { ...postFieldFilters };
    if (vals.length === 0) delete next[key];
    else next[key] = vals;
    onPostFieldFiltersChange(next);
  }

  function updateUserField(key: string, vals: string[]) {
    const next = { ...userFieldFilters };
    if (vals.length === 0) delete next[key];
    else next[key] = vals;
    onUserFieldFiltersChange(next);
  }

  function toggleArrayItem<T>(arr: T[], item: T): T[] {
    return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">検索・フィルター</h2>
          <div className="flex gap-2">
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={onReset}
                className="text-sm text-danger-600 bg-transparent border-none cursor-pointer"
              >
                リセット
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-slate-600 px-3 py-1 border border-slate-300 rounded bg-white cursor-pointer"
            >
              閉じる
            </button>
          </div>
        </div>

        <div className="p-4 space-y-5">
          {/* キーワード */}
          <Section title="キーワード">
            <input
              type="text"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              placeholder="タイトル・コメント・投稿者名"
              className="w-full min-h-touch px-3 border border-slate-300 rounded text-base"
            />
          </Section>

          {/* マーカーの見方 (colorRule の凡例) */}
          {legendEntries.length > 0 && (
            <Section title="マーカーの見方">
              <div className="grid grid-cols-2 gap-2">
                {legendEntries.map((e) => (
                  <div key={e.label} className="flex items-center gap-2">
                    <span
                      className="inline-block w-5 h-5 rounded-full border border-white shadow"
                      style={{ background: e.color }}
                    />
                    <span className="text-sm text-slate-700">
                      {e.icon ? `${e.icon} ` : ''}
                      {e.label}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* セッション */}
          {sessions.length > 0 && (
            <Section title="セッション">
              <div className="flex flex-wrap gap-2">
                {sessions.map((s) => {
                  const active = sessionIds.includes(s.id);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => onSessionIdsChange(toggleArrayItem(sessionIds, s.id))}
                      className={`px-3 py-1.5 rounded-full text-xs border transition cursor-pointer ${
                        active
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-slate-700 border-slate-300'
                      }`}
                    >
                      {active && '✓ '}
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </Section>
          )}

          {/* 投稿項目 */}
          {filterablePostFields.length > 0 && (
            <Section title="投稿項目">
              {filterablePostFields.map((f) => (
                <FilterChips
                  key={f.key}
                  label={f.label}
                  options={getFieldOptions(f)}
                  selected={postFieldFilters[f.key] ?? []}
                  onChange={(v) => updatePostField(f.key, v)}
                />
              ))}
            </Section>
          )}

          {/* 投稿者の属性 */}
          {filterableUserFields.length > 0 && (
            <Section title="投稿者の属性">
              {filterableUserFields.map((f) => (
                <FilterChips
                  key={f.key}
                  label={f.label}
                  options={getFieldOptions(f)}
                  selected={userFieldFilters[f.key] ?? []}
                  onChange={(v) => updateUserField(f.key, v)}
                />
              ))}
            </Section>
          )}

          {/* AI解析結果 (M2 category master chip。複合コード majorCd-categoryCd で一意化) */}
          {categoryOptions.length > 0 && (
            <Section title="AI解析結果">
              <FilterChips
                label="カテゴリ"
                options={categoryOptions}
                selected={selectedCategoryCodes}
                onChange={onSelectedCategoryCodesChange}
              />
              {selectedCategoryCodes.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1">※ AI 未解析の投稿は表示されません</p>
              )}
            </Section>
          )}

          {/* 日付範囲 */}
          <Section title="日付範囲">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                aria-label="開始日"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="min-h-touch px-2 border border-slate-300 rounded text-sm"
              />
              <input
                type="date"
                aria-label="終了日"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="min-h-touch px-2 border border-slate-300 rounded text-sm"
              />
            </div>
          </Section>

          {/* その他 */}
          <Section title="その他">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasPhoto === true}
                  onChange={() => onHasPhotoChange(hasPhoto === true ? null : true)}
                  className="w-5 h-5"
                />
                写真あり
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span>最小いいね</span>
                <input
                  type="number"
                  min={0}
                  value={minAgreeCount}
                  onChange={(e) => onMinAgreeCountChange(Number(e.target.value))}
                  className="w-16 min-h-touch px-2 border border-slate-300 rounded text-sm"
                />
              </label>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

/**
 * 自分の投稿のみ表示 toggle (old2 MainPage OnlyMineToggle L212-231 逐語、配色のみ v2 preset)。
 * AppHome / MapPage 両 header で共有。
 */
export function OnlyMineToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className="flex items-center gap-1 text-xs select-none cursor-pointer min-h-touch px-1"
      title="自分の投稿のみ表示"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary-600"
      />
      <span className="whitespace-nowrap leading-tight">
        <span className="hidden sm:inline">自分の投稿のみ表示</span>
        <span className="sm:hidden">自分のみ</span>
      </span>
    </label>
  );
}

/**
 * 🔍 フィルタ button + activeFilterCount 赤丸 badge (old2 MainPage FilterButton L233-249 逐語)。
 * AppHome / MapPage 両 header で共有。
 */
export function FilterButton({
  activeFilterCount,
  onClick,
}: {
  activeFilterCount: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-w-touch min-h-touch text-xl relative bg-transparent border-none cursor-pointer"
      aria-label="検索・フィルター"
    >
      🔍
      {activeFilterCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-danger-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
          {activeFilterCount}
        </span>
      )}
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-slate-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function FilterChips({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: FieldOption[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((x) => x !== value) : [...selected, value]);
  }
  if (options.length === 0) return null;
  return (
    <div className="mb-2">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="flex flex-wrap gap-1 mt-1">
        {options.map((o) => {
          const active = selected.includes(o.value);
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => toggle(o.value)}
              className={`px-2 py-1 rounded text-xs border transition cursor-pointer ${
                active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-slate-700 border-slate-200'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
